import routes from '@/config/routes';
import { Product } from '@/types';
import ImageComponent from '../ui/image';
import AnchorLink from '../ui/links/anchor-link';
import placeholder from '@/assets/images/placeholders/product.svg';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { ArrowRight } from 'lucide-react';
import { LightningIcon } from '../icons/lightning-icon';
import { useNSFWBlur } from '@/lib/hooks/use-nsfw-blur';
import { cn } from '@/lib/utils';
import NSFWToggle from '../nsfw/nsfw-toggle';
import FavoriteButton from '../favorite/favorite-button';

export default function Card({ product }: { product: Product }) {
  const { shouldBlur } = useNSFWBlur(product);
  const { name, slug, image, ai_model, ai_model_version_slug } = product ?? {};

  return (
    <div className="relative">
      <AnchorLink 
        href={routes.productUrl(slug)}
        className={cn(shouldBlur && "pointer-events-none")}
      >
        <motion.div
          className="group relative aspect-[4/5] overflow-hidden rounded-lg bg-gray-200 shadow"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ 
            scale: 1.05,
            boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)"
          }}
          transition={{ 
            duration: 0.3,
            ease: "easeOut"
          }}
        >
          <div className="absolute right-2 top-2 z-[9]">
            <FavoriteButton 
              productId={product.id}
              size="xs"
              iconOnly
              className="!bg-black/40 hover:!bg-black/60 !border-white/20 backdrop-blur-sm text-white/90 dark:text-white/90 hover:text-white"
            />
          </div>

          <ImageComponent
            width={344}
            height={344}
            alt={`${ai_model.name} ${ai_model_version_slug} Prompt for ${product.name}`}
            title={`View ${ai_model.name} Prompt for ${product.name}`}
            className="h-full w-full object-cover"
            src={image?.original ?? placeholder}
            loading="lazy"
          />
          <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/60 via-black/30 to-transparent p-2">
            <p className="mb-1 text-xs font-medium text-white/90">
              {name}
            </p>
            <Button
              className="group relative w-full overflow-hidden rounded-md bg-white/5 dark:bg-white/5 dark:hover:bg-white/10 backdrop-blur-md 
                duration-300 transition-all hover:bg-white/10"
            >
              <div className="absolute inset-0 rounded-md bg-white/10 backdrop-blur-sm" />
              <span className="absolute inset-0 rounded-lg bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 opacity-0 blur group-hover:animate-shine-shadow group-hover:opacity-100" />
              <div className="relative z-10 flex items-center justify-between px-2 py-1">
                <div className="flex items-center gap-1">
                  <LightningIcon className="mr-2 h-4 w-4 rounded-full border-1 border-white text-white" />
                  <span className="text-xs font-medium text-white">
                    Try Generator
                  </span>
                </div>
                <ArrowRight className="h-3 w-3 text-white duration-300 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Button>
          </div>
        </motion.div>
      </AnchorLink>

      {shouldBlur && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 backdrop-blur-md">
          <div className="absolute inset-0 bg-dot-pattern opacity-5" />
          <div className="relative flex flex-col items-center gap-2">
            <div className="h-1.5 w-12 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 mb-1" />
            <p className="text-sm font-medium text-foreground/90">Adult Content</p>
            <div className="bg-background/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg border border-violet-500/20">
              <NSFWToggle size="lg" className="min-w-[120px]" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
